import React, { useState, useEffect, useCallback } from 'react';
import { Grid, Plus, X, Save, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../api';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
const PERIODS = ['Period 1', 'Period 2', 'Period 3', 'Break', 'Period 4', 'Period 5', 'Period 6', 'Period 7'];

const DAY_SHORT = { Monday: 'Mon', Tuesday: 'Tue', Wednesday: 'Wed', Thursday: 'Thu', Friday: 'Fri' };

export default function TimetablePage() {
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [slots, setSlots] = useState([]);
  const [editModal, setEditModal] = useState(null); // { day, period }
  const [form, setForm] = useState({ subject_name: '', teacher_id: '', teacher_name: '', start_time: '', end_time: '' });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get('/academics/classes'),
      api.get('/academics/subjects'),
      api.get('/teachers'),
    ]).then(([cls, sub, tch]) => {
      if (cls.success) setClasses(cls.data);
      if (sub.success) setSubjects(sub.data);
      if (tch.success) setTeachers(tch.data || []);
    }).catch(() => {});
  }, []);

  const loadTimetable = useCallback(async () => {
    if (!selectedClass) return;
    setLoading(true);
    try {
      const d = await api.get(`/timetable/class/${selectedClass.id}`);
      if (d.success) setSlots(d.data);
    } catch {
      setSlots([]);
    } finally {
      setLoading(false);
    }
  }, [selectedClass]);

  useEffect(() => { loadTimetable(); }, [loadTimetable]);

  const getSlot = (day, period) => slots.find(s => s.day === day && s.period === period);

  const openEdit = (day, period) => {
    const existing = getSlot(day, period);
    setForm({
      subject_name: existing?.subject_name || '',
      teacher_id: existing?.teacher_id || '',
      teacher_name: existing?.teacher_name || '',
      start_time: existing?.start_time || '',
      end_time: existing?.end_time || '',
    });
    setEditModal({ day, period });
  };

  const handleSave = async () => {
    if (!selectedClass || !editModal) return;
    setSaving(true);
    try {
      const teacher = teachers.find(t => t.id === form.teacher_id);
      await api.post('/timetable/slots', {
        class_id: selectedClass.id,
        class_name: selectedClass.name,
        day: editModal.day,
        period: editModal.period,
        subject_name: form.subject_name,
        teacher_id: form.teacher_id,
        teacher_name: teacher ? `${teacher.first_name} ${teacher.last_name}` : form.teacher_name,
        start_time: form.start_time,
        end_time: form.end_time,
      });
      toast.success('Slot saved');
      setEditModal(null);
      loadTimetable();
    } catch (err) {
      toast.error(err?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    if (!selectedClass || !editModal) return;
    setSaving(true);
    try {
      await api.delete(`/timetable/slots?class_id=${selectedClass.id}&day=${editModal.day}&period=${encodeURIComponent(editModal.period)}`);
      toast.success('Slot cleared');
      setEditModal(null);
      loadTimetable();
    } catch (err) {
      toast.error(err?.detail || 'Failed to clear');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-5" data-testid="timetable-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Timetable</h1>
          <p className="text-sm text-slate-500 mt-0.5">Manage weekly class schedules</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <select
              value={selectedClass?.id || ''}
              onChange={e => {
                const cls = classes.find(c => c.id === e.target.value);
                setSelectedClass(cls || null);
              }}
              className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white pr-8 appearance-none focus:ring-2 focus:ring-indigo-500 outline-none"
              data-testid="class-select"
            >
              <option value="">Select a class</option>
              {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <ChevronDown className="w-4 h-4 text-slate-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>
        </div>
      </div>

      {!selectedClass ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <Grid className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="font-medium text-slate-500">Select a class to view or edit its timetable</p>
        </div>
      ) : loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
          <div className="p-4 border-b border-slate-100 flex items-center gap-2">
            <Grid className="w-4 h-4 text-indigo-500" />
            <span className="font-semibold text-slate-700 text-sm">{selectedClass.name} — Weekly Schedule</span>
          </div>
          <table className="w-full text-sm min-w-[600px]">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 w-24">Period</th>
                {DAYS.map(d => (
                  <th key={d} className="text-center px-3 py-2.5 text-xs font-semibold text-slate-500">{DAY_SHORT[d]}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {PERIODS.map(period => (
                <tr key={period} className={period === 'Break' ? 'bg-amber-50' : 'hover:bg-slate-50 transition-colors'}>
                  <td className="px-4 py-2.5 font-medium text-xs text-slate-500 whitespace-nowrap">
                    {period === 'Break' ? '☕ Break' : period}
                  </td>
                  {DAYS.map(day => {
                    const slot = getSlot(day, period);
                    if (period === 'Break') {
                      return <td key={day} className="px-3 py-2.5 text-center text-xs text-amber-500 font-medium">Break</td>;
                    }
                    return (
                      <td key={day} className="px-2 py-1.5">
                        <button
                          onClick={() => openEdit(day, period)}
                          className={`w-full min-h-[52px] rounded-lg border text-xs transition-all text-left px-2.5 py-2 ${
                            slot?.subject_name
                              ? 'bg-indigo-50 border-indigo-200 hover:bg-indigo-100'
                              : 'bg-slate-50 border-slate-200 hover:bg-slate-100 hover:border-slate-300'
                          }`}
                          data-testid={`slot-${day}-${period}`}
                        >
                          {slot?.subject_name ? (
                            <>
                              <div className="font-semibold text-indigo-700 truncate">{slot.subject_name}</div>
                              {slot.teacher_name && (
                                <div className="text-indigo-400 text-[10px] truncate mt-0.5">{slot.teacher_name}</div>
                              )}
                              {slot.start_time && (
                                <div className="text-slate-400 text-[10px]">{slot.start_time}{slot.end_time ? `–${slot.end_time}` : ''}</div>
                              )}
                            </>
                          ) : (
                            <span className="text-slate-300 flex items-center gap-1">
                              <Plus className="w-3 h-3" /> Add
                            </span>
                          )}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit Modal */}
      {editModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm" data-testid="slot-modal">
            <div className="flex items-center justify-between p-5 border-b border-slate-100">
              <div>
                <h3 className="font-bold text-slate-800">{editModal.period}</h3>
                <p className="text-xs text-slate-500">{editModal.day} · {selectedClass?.name}</p>
              </div>
              <button onClick={() => setEditModal(null)} className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-100">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1 block">Subject</label>
                <select
                  value={form.subject_name}
                  onChange={e => setForm(f => ({ ...f, subject_name: e.target.value }))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                  data-testid="slot-subject-select"
                >
                  <option value="">— Select subject —</option>
                  {subjects.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1 block">Teacher</label>
                <select
                  value={form.teacher_id}
                  onChange={e => setForm(f => ({ ...f, teacher_id: e.target.value }))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                  data-testid="slot-teacher-select"
                >
                  <option value="">— Select teacher —</option>
                  {teachers.map(t => <option key={t.id} value={t.id}>{t.first_name} {t.last_name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1 block">Start Time</label>
                  <input
                    type="time"
                    value={form.start_time}
                    onChange={e => setForm(f => ({ ...f, start_time: e.target.value }))}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                    data-testid="slot-start-time"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1 block">End Time</label>
                  <input
                    type="time"
                    value={form.end_time}
                    onChange={e => setForm(f => ({ ...f, end_time: e.target.value }))}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                    data-testid="slot-end-time"
                  />
                </div>
              </div>
            </div>
            <div className="p-5 border-t border-slate-100 flex gap-2">
              <button
                onClick={handleClear}
                disabled={saving}
                className="flex-1 border border-slate-200 text-slate-600 text-sm py-2 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
                data-testid="clear-slot-btn"
              >
                Clear
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !form.subject_name}
                className="flex-1 bg-indigo-600 text-white text-sm py-2 rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5"
                data-testid="save-slot-btn"
              >
                <Save className="w-3.5 h-3.5" />
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
